"use client";

import React, { useState } from "react";
import Link from "next/link";
import { ChevronDown, ChevronUp } from "lucide-react";
import { FileText } from "lucide-react";
import Container from "../Container";

const faqs = [
  {
    question: "Who is Seozast designed for?",
    answer: (
      <>
        Seozast is designed for startups, bloggers, website owners, and marketing agencies looking to boost organic traffic with automated SEO content. From small blogs (Basic, 20 articles/month) to high-traffic platforms (Enterprise, 100 articles/month), Seozast adapts to your needs, one-click GitHub publishing.
      </>
    ),
  },
  {
    question: "How does Seozast help me grow my organic traffic?",
    answer: (
      <>
        Seozast generates SEO-optimized articles using manual or automated keywords. It includes automated scraping for relevant data a dashboard to manage content and one-click GitHub publishing. Pro and Enterprise plans offer priority support to maximize results.
      </>
    ),
  },
  {
    question: "What’s included in the subscription plan?",
    answer: (
      <>
        All plans include SEO article generation, a full dashboard, dynamic sitemap, and manual keyword input. Toggle automation to add automated keywords (Google Ads API) and scraping (Puppeteer). Choose from:
        <ul className="mt-2 space-y-1 text-gray-100">
          <li className="flex items-start">
            <span className="text-emerald-400 mr-2">✔</span> Basic: 20 articles, 2 sites
          </li>
          <li className="flex items-start">
            <span className="text-emerald-400 mr-2">✔</span> Pro: 40 articles, 4 sites, priority support
          </li>
          <li className="flex items-start">
            <span className="text-emerald-400 mr-2">✔</span> Enterprise: 100 articles, 6 sites, priority support
          </li>
        </ul>
      </>
    ),
  },
  {
    question: "Can I export the articles for use elsewhere?",
    answer: (
      <>
        Yes, you can export generated articles as text, Word or markdown from the dashboard. Use them in your CMS (WordPress, Webflow, etc.) or other platforms without restrictions.
      </>
    ),
  },
  {
    question: "Can I use Seozast to publish on my own domain?",
    answer: (
      <>
        Absolutely. Publish articles directly on your domain using Seozast’s dynamic routes (<code>/blog/[slug]</code>) or export them for integration into your site. The dynamic sitemap ensures Google indexes your content quickly.
      </>
    ),
  },
  {
    question: "Can I customize the articles or topics before generation?",
    answer: (
      <>
        Yes, change the automated keywords manually. Adjust topics, titles, and parameters (length) in the dashboard before generating articles.
      </>
    ),
  },
  {
    question: "Do I need technical skills to use Seozast?",
    answer: (
      <>
        No, Seozast is user-friendly with an intuitive dashboard and no technical skills required. Automation (scraping, keywords) is preconfigured, and we provide detailed documentation for initial setup.
      </>
    ),
  },
  // {
  //   question: "Does Seozast support languages other than English?",
  //   answer: (
  //     <>
  //       Yes, Seozast supports multiple languages via AI integration. Generate content in 63 languages.
  //     </>
  //   ),
  // },
  {
    question: "Do I fully own the articles and images created?",
    answer: (
      <>
        You fully own the articles generated by Seozast, and you can use them freely, provided they comply with applicable laws and do not infringe third-party rights. Images are sourced from Pexels under the <a href="https://pexels.com/license" target="_blank" className="text-blue-500 underline">Pexels License</a>, which allows free use for personal and commercial purposes without attribution. However, you do not own the images, and certain restrictions apply (e.g., you cannot resell them as standalone products). Always review the Pexels License for details.
      </>
    ),
  },
  {
    question: "Is the content original and free from plagiarism?",
    answer: (
      <>
        Seozast uses AI to generate original content, verified to avoid plagiarism. Automated scraping cleans and restructures data to ensure uniqueness.
      </>
    ),
  },
  {
    question: "How can I track the performance of my articles?",
    answer: (
      <>
        The dashboard displays key metrics like articles generated and monthly limits. Integrate external tools (Google Analytics, Search Console) to track traffic and keyword rankings.
      </>
    ),
  },
  {
    question: "Is there a free trial or sample content available?",
    answer: (
      <>
        We don’t offer a free trial, so we recommend to try with the cheapest plan first. You can also view sample articles on our site or{" "}
        <Link href="/contact" className="text-emerald-400 hover:underline">
          contact us
        </Link>{" "}
        for a personalized demo before subscribing.
      </>
    ),
  },
];

const FAQ = () => {
  const [openQuestionIndex, setOpenQuestionIndex] = useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    setOpenQuestionIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="py-20 text-black">
      <Container>
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          Frequently Asked Questions
        </h2>
        <div className="space-y-6 max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <div key={index}>
              <dt
                className={`text-lg font-semibold mb-2 flex justify-between items-center cursor-pointer ${openQuestionIndex === index ? "text-emerald-500" : "text-black"
                  }`}
                onClick={() => toggleQuestion(index)}
              >
                <span>{faq.question}</span>
                {openQuestionIndex === index ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </dt>
              {openQuestionIndex === index && (
                <dd className="text-sm sm:text-md text-black leading-relaxed">
                  {faq.answer}
                </dd>
              )}
            </div>
          ))}
          {/* <div className="mt-10 bg-emerald-50 border border-emerald-200 rounded-xl p-6 flex items-center gap-4 shadow-sm">
            <FileText className="text-emerald-600" size={32} />
            <div className="text-lg">
              <span className="font-semibold text-emerald-700">Want to see Seozast in action?</span>{" "}
              <Link href="/articles" className="text-black font-semibold underline hover:text-emerald-700">
                Check sample articles here.
              </Link>
            </div>
          </div> */}
          <p className="text-center mt-8">
            Have another question?{" "}
            <Link href="/contact" className="text-emerald-400 hover:underline">
              Contact us
            </Link>.
          </p>
          <div className="mt-16 border-t border-gray-200 pt-8 text-center">
            <h3 className="text-2xl font-bold text-emerald-600 mb-4">
              Sample SEO Articles
            </h3>
            <p className="text-gray-700 mb-6">
              Curious what kind of content Seozast can create?
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
              <Link
                href="/articles/diving-deeper-into-nextjs-render-methods-optimizing-page-rendering-for-seo-success"
                className="block p-4 rounded-lg border border-gray-200 hover:border-emerald-400 hover:shadow transition"
              >
                <h4 className="text-emerald-700 font-semibold mb-1">
                  Diving Deeper into Next.js Render Methods: Optimizing Page Rendering for SEO Success
                </h4>
                <p className="text-sm text-gray-600">
                  The way to render Next.js pages
                </p>
              </Link>

              <Link
                href="/articles/the-power-of-automation-in-seozast-how-to-streamline-your-seo-efforts"
                className="block p-4 rounded-lg border border-gray-200 hover:border-emerald-400 hover:shadow transition"
              >
                <h4 className="text-emerald-700 font-semibold mb-1">
                  The Power Of Automation In Seozast How To Streamline Your Seo Efforts
                </h4>
                <p className="text-sm text-gray-600">
                  The power of SEO automation
                  {/*       AI vs Human SEO Content  Which one ranks better? We tested both. */}
                </p>
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default FAQ;